<template>
  <b-container fluid>
    <h1 class="m-3">
      {{ title }}
    </h1>
    <b-row class="px-3 justify-content-between">
      <b-col cols="12">
        <div class="d-flex flex-wrap flex-lg-nowrap filter-gap">

          <div class="filter-width">
            <filter-site-picker :use-default-value="true" :multiple="true" :select-all="true" operator="equals"
              :item-key="siteIdFilterProperty" :item-label="siteLabelFilterProperty"
              :item-currency="siteCurrencyFilterProperty" :group-by-key="groupKey" :group-by-label="groupLabel"
              @filterWithIdChanged="siteFilterChanged($event)" />
          </div>

          <div class="d-flex filter-gap flex-wrap">
            <date-range-dual-picker :time-range-value="timeRangeValue"
              @dateRangeDualFiltersUpdated="dateRangeDualFiltersUpdated($event)" />

            <time-range-picker :use-default-value="true" @timeRangeFilterChanged="timeRangeFilterUpdated($event)" />
          </div>

          <b-dropdown toggle-class="dropdown-style" right :text="$t('export.export')"
            class="ml-lg-auto align-self-center align-self-lg-start">
            <b-dropdown-item class="mb-2" :disabled="!dataReadyTables" @click="downloadPdf()">
              <font-awesome-icon icon="file-pdf" class="mr-2 export-icon-pdf" />
              {{ $t("export.PDF") }}
            </b-dropdown-item>
            <b-dropdown-item class="mb-2" :disabled="!dataReadyTables" @click="downloadExcel()">
              <font-awesome-icon icon="file-excel" class="mr-2 export-icon-excel" />
              {{ $t("export.excel") }}
            </b-dropdown-item>
            <b-dropdown-item :disabled="!dataReadyTables" @click="downloadCsv()">
              <font-awesome-icon icon="file-csv" class="mr-2 export-icon" />
              {{ $t("export.csv") }}
            </b-dropdown-item>
          </b-dropdown>

        </div>
      </b-col>
    </b-row>
    <table-renderer v-for="data of tablesData" :key="data.tableNumber" :data-table="data.dataTable" ref="tableRenderers"
      :dimension-fields="dimensionColumnsTables" :measure-fields="measureColumnsTables" :group-headers="groupHeaders"
      :table-header="data.tableHeader" :default-sorted-column="defaultSortedColumnTables" :totals-data="data.totalsData"
      :data-ready="dataReadyTables" :default-sorted-desc="true" />
  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import {
  columnTypeEnum,
  groupKeyEnum,
  calculatedColumnsEnum,
} from "@/model/columnsEnums";
import { dateFormatEnum } from "@/filter/dateFormat";
import FilterSitePicker from "@/components/picker/FilterSitePicker.vue";
import DateRangeDualPicker from "@/components/picker/DateRangeDualPicker.vue";
import TimeRangePicker from "@/components/picker/TimeRangePicker.vue";
import { GamesCompare } from "@/router/routeNames";
import _ from "lodash";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: GamesCompare,
  mixins: [abortMixin],
  components: {
    FilterSitePicker,
    DateRangeDualPicker,
    TimeRangePicker,
    TableRenderer,
  },
  data() {
    return {
      /** Tableau d'objets des sites sélectionnés, indiquant leur id et leur label */
      siteValues: [],
      /** Filtre venant du FilterPicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "Site.label",
       *      operator: "equals",
       *      values: ["Site 1"]
       *    }
       */
      siteFilter: null,
      siteIdFilterProperty: dimensionEnum.SITE,
      siteLabelFilterProperty: dimensionEnum.SITE_NAME,
      siteCurrencyFilterProperty: dimensionEnum.SITE_CURRENCY,
      groupKey: dimensionEnum.SITE_SITE_CATEGORY,
      groupLabel: dimensionEnum.SITE_SITE_CATEGORY_NAME,
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      /** Filtre venant du DateRangePicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "BusinessDate.id",
       *      operator: "inDateRange",
       *      values: ["2020-09-23", "2020-09-26"]
       *    }
       */
      dateRangeFilter1: null,
      dateRangeFilter2: null,
      /** Libellé des dates sélectionnées, telles qu'affichées dans le DateRangePicker */
      dateRangeStr1: "",
      dateRangeStr2: "",
      /** Libellé de la période (dates) sélectionnée */
      labelDatePeriod1: "",
      labelDatePeriod2: "",
      /** Filtre venant du TimeRangePicker, tel qu'attendu par cubeJs.
       * NB : Il faut lister les valeurs possibles au lieu d'utiliser des filtres >,< car cubeJs ne supporte pas encore bien le OR (pour les horaires > 23)
       * Ex :
       *    {
       *      dimension: dimensionEnum.TIME_HOUR,
       *      operator: "equals",
       *      values: ["18", "19", "20", "21", "22", "23", "0", "1"]
       *    }
       */
      timeRangeFilter: null,
      isTimeFullRange: false,
      timeRangeValue: null,
      dataReadyTables: false,
      dimensionColumnsTables: [],
      measureColumnsTables: [],
      defaultSortedColumnTables: "",
      tablesData: [],
    };
  },
  computed: {
    isDefaultQueryReady() {
      return (
        this.siteFilter &&
        this.dateRangeFilter1 &&
        this.dateRangeFilter2 &&
        (this.timeRangeFilter || this.isTimeFullRange)
      );
    },
    title() {
      return this.$t("title.games_compare");
    },
  },
  watch: {
    // TODO custom column header for periods : ça fonctionne en décommentant dateRangeStr1 et dateRangeStr2, mais ça élargi trop les colonnes, à discuter
    // dateRangeStr1(newValue, oldValue) {
    //   const self = this;
    //   self.measureColumnsTables = self.measureColumnsTables.map(col => {
    //     if (col.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1 || col.key == calculatedColumnsEnum.PERCENT_PER_GAME_PERIOD1) {
    //       col.label = newValue;
    //     }
    //     return col;
    //   });
    // },
    // dateRangeStr2(newValue, oldValue) {
    //   const self = this;
    //   self.measureColumnsTables = self.measureColumnsTables.map(col => {
    //     if (col.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2 || col.key == calculatedColumnsEnum.PERCENT_PER_GAME_PERIOD2) {
    //       col.label = newValue;
    //     }
    //     return col;
    //   });
    // }
  },
  mounted() {
    this.initDisplayTables();
  },
  methods: {
    siteFilterChanged(value) {
      this.siteValues = value.values;
      this.siteFilter = value.idFilter;
      this.updateTables();
    },
    dateRangeDualFiltersUpdated(value) {
      if (value && value.dateRangePickerData1 && value.dateRangeFilterData2) {
        this.dateRangeFilter1 = value.dateRangePickerData1.dateRangeFilter;
        this.dateRangeStr1 = value.dateRangePickerData1.dateRangeStr;
        this.labelDatePeriod1 = value.dateRangePickerData1.labelPeriod;
        this.dateRangeFilter2 = value.dateRangeFilterData2.dateRangeFilter;
        this.dateRangeStr2 = value.dateRangeFilterData2.dateRangeStr;
        this.labelDatePeriod2 = value.dateRangeFilterData2.labelPeriod;
      } else {
        this.dateRangeFilter1 = null;
        this.dateRangeStr1 = "";
        this.labelDatePeriod1 = "";
        this.dateRangeFilter2 = null;
        this.dateRangeStr2 = "";
        this.labelDatePeriod2 = "";
      }
      this.updateTables();
    },
    timeRangeFilterUpdated(value) {
      if (value) {
        this.timeRangeValue = value;
        this.timeRangeFilter = value.timeRangeFilter;
        this.isTimeFullRange = value.isFullRange;
      } else {
        this.timeRangeValue = null;
        this.timeRangeFilter = null;
        this.isTimeFullRange = false;
      }
      this.updateTables();
    },
    updateTables() {
      if (this.isDefaultQueryReady) {
        let commonFilters = [this.siteFilter];
        if (!this.isTimeFullRange) {
          commonFilters.push(this.timeRangeFilter);
        }
        const dimensions = [
          dimensionEnum.GAME,
          dimensionEnum.GAME_NAME,
          dimensionEnum.GAME_CODE,
          dimensionEnum.SITE,
          dimensionEnum.SITE_NAME,
          dimensionEnum.SITE_CURRENCY,
        ];
        this.updateTableList(
          commonFilters,
          this.dateRangeFilter1,
          this.dateRangeFilter2,
          dimensions
        );
      } else {
        this.dataReadyTables = false;
      }
    },
    async updateTableList(
      commonFilters,
      dateRangeFilter1,
      dateRangeFilter2,
      dimensions
    ) {
      try {
        const { signal } = this.resetAbortController();
        var self = this;
        self.dataReadyTables = false;

        self.tablesData = [];
        // Initialisation des objets contenant toutes les données pour chaque site :
        // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
        self.siteValues.forEach((site, i) => {
          var table = {
            tableNumber: i.toString(),
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
            totalsData: {},
          };
          self.tablesData.push(table);
        });

        var queries = [];
        // getFilterGameNotFree permet de ne récupérer que les jeux non gratuits
        // ie. les smart touchs qui servent juste à entrer / sortir d'une zone
        // et sont représentés en BDD comme un jeu sans unités à dépenser
        var baseFilters = [...commonFilters, TableTools.Helper.getFilterGameNotFree()];

        queries.push({
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD1,
          query: {
            measures: [
              mesureEnum.GAMEHISTORY_TOKEN_PRICE,
              mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
              mesureEnum.GAMEHISTORY_BONUSTOKEN_PRICE_HT,
            ],
            dimensions: dimensions,
            timeDimensions: dateRangeFilter1 ? [dateRangeFilter1] : [],
            filters: [...baseFilters],
          },
        });

        queries.push({
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD2,
          query: {
            measures: [
              mesureEnum.GAMEHISTORY_TOKEN_PRICE,
              mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
              mesureEnum.GAMEHISTORY_BONUSTOKEN_PRICE_HT,
            ],
            dimensions: dimensions,
            timeDimensions: dateRangeFilter2 ? [dateRangeFilter2] : [],
            filters: [...baseFilters],
          },
        });

        var resultSets = await TableTools.Helper.doMultipleRequest(queries, { signal });
        var mergedResults = TableTools.Helper.mergeResultsByCommonDimensions(
          resultSets,
          dimensions
        );

        // Extraire la devise du premier résultat
        const siteCurrency = mergedResults.length > 0 ? (mergedResults[0][dimensionEnum.SITE_CURRENCY] || 'EUR') : 'EUR';
        self.initDisplayTables(siteCurrency);

        // Modification des labels de périodes. On affiche la date directement
        // étant donnée que les filtres ne sont plus directement visible sur la page
        // on les affiche ici pour plus de lisibilité
        self.measureColumnsTables.forEach((mesureColmun) => {
          if (
            mesureColmun.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1 ||
            mesureColmun.key == calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1
          ) {
            mesureColmun.label = TableTools.Helper.formatDateLabel(dateRangeFilter1.dateRange);
          }
          else if (
            mesureColmun.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2 ||
            mesureColmun.key == calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2
          ) {
            mesureColmun.label = TableTools.Helper.formatDateLabel(dateRangeFilter2.dateRange);
          }
        })

        // Complétion des objets avec les données reçues pour chaque site
        self.siteValues.forEach((site) => {
          var table = self.tablesData.find(
            (tab) => tab.tableHeaderId === site.id
          );
          table.dataTable = mergedResults.filter(
            (result) => result[dimensionEnum.SITE] === site.id
          );
          // Ajouter la devise à chaque ligne pour le formatage
          table.dataTable.forEach(row => {
            row["Site.currency"] = siteCurrency;
          });
          table.dataTable = self.completeInvoiceGroup(table.dataTable);
          table.totalsData = TableTools.Helper.calculateTotalsData(
            table.dataTable,
            [
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1,
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2,
              calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1,
              calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2,
            ]
          );
          table.dataTable = self.completePercentageGroupAndAddStyles(
            table.dataTable,
            table.totalsData
          );
          table.totalsData = self.completeTotals(table.totalsData);
        });

        self.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    /** Initialise ce qui concerne l'affichage commun à tous les tableaux de ce rapport : quelles colonnes doivent réellement être affichées, dans quel ordre
     */
    initDisplayTables(siteCurrency = 'EUR') {
      const self = this;
      self.dimensionColumnsTables = TableTools.Helper.constructColumnTable(
        [
          calculatedColumnsEnum.RANK,
          dimensionEnum.GAME_NAME,
          dimensionEnum.GAME_CODE
        ],
        null,
        columnTypeEnum.DIMENSION,
        true,
        null
      );
      self.measureColumnsTables = TableTools.Helper.constructColumnTable(
        [
          calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1,
          calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2,
          calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT,
          calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1,
          calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2,
          calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS,
        ],
        null,
        columnTypeEnum.MEASURE,
        true,
        null,
        false,
        siteCurrency
      );

      self.measureColumnsTables = self.measureColumnsTables.map((col) => {
        if (
          col.key == calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS ||
          col.key == calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT
        ) {
          col.tdClass = "col-align-left";
        }
        return col;
      });

      self.groupHeaders = [
        { nbOfMeasures: 3, label: self.$t("group.invoiceAmountHt") },
        { nbOfMeasures: 3, label: self.$t("group.invoiceAmount") },
      ];

      self.defaultSortedColumnTables = calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1;
    },
    completeInvoiceGroup(dataTable) {
      let completeDataTable = dataTable.map((data) => {
        // Recette TTC
        const purchaseAmount1 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.GAMEHISTORY_TOKEN_PRICE,
            groupKeyEnum.HIDDENGROUP_PERIOD1
          )
          ];
        const gameRevenue1 = purchaseAmount1 || 0;
        data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] = gameRevenue1;

        const purchaseAmount2 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.GAMEHISTORY_TOKEN_PRICE,
            groupKeyEnum.HIDDENGROUP_PERIOD2
          )
          ];
        const gameRevenue2 = purchaseAmount2 || 0;
        data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] = gameRevenue2;

        data[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] = gameRevenue2
          ? (gameRevenue1 / gameRevenue2 - 1) * 100
          : this.$t("Display.notSignificant");

        // Recette HT
        const purchaseAmountHt1 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
            groupKeyEnum.HIDDENGROUP_PERIOD1
          )
          ];
        const gameRevenueHt1 = purchaseAmountHt1 || 0;
        data[calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1] = gameRevenueHt1;

        const purchaseBonusAmountHt1 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.GAMEHISTORY_BONUSTOKEN_PRICE_HT,
            groupKeyEnum.HIDDENGROUP_PERIOD1
          )
          ];
        const gameBonusRevenueHt1 = purchaseBonusAmountHt1 || 0;

        const purchaseAmountHt2 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
            groupKeyEnum.HIDDENGROUP_PERIOD2
          )
          ];
        const gameRevenueHt2 = purchaseAmountHt2 || 0;
        data[calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2] = gameRevenueHt2;

        data[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT] = gameRevenueHt2
          ? (gameRevenueHt1 / gameRevenueHt2 - 1) * 100
          : this.$t("Display.notSignificant");

        // Classement par popularité
        const popularityByRevenue =
          (gameRevenueHt1 || 0) +
          (gameBonusRevenueHt1 || 0);

        data[calculatedColumnsEnum.POPULARITY] = popularityByRevenue
          ? popularityByRevenue
          : 0;

        return data;
      });

      // Classement par popularité
      completeDataTable = _.orderBy(
        completeDataTable,
        (data) => data[calculatedColumnsEnum.POPULARITY] || 0,
        "desc"
      );
      var i = 1;
      completeDataTable.forEach((data) => {
        // On n'attribue pas de rang aux lignes de groupes, différenciées par un nom de jeu vide
        if (data.groupLevel === undefined) {
          data[calculatedColumnsEnum.RANK] = i;
          i++;
        }
      });

      return completeDataTable;
    },
    completePercentageGroupAndAddStyles(dataTable, totalsData) {
      return dataTable.map((data) => {
        const gameRevenue1 =
          data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] || 0;
        const totalRevenue1 =
          totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] || 0;
        const percentPeriod1 = totalRevenue1
          ? (gameRevenue1 * 100) / totalRevenue1
          : 0;
        data[calculatedColumnsEnum.PERCENT_PER_GAME_PERIOD1] = percentPeriod1;

        const gameRevenue2 =
          data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] || 0;
        const totalRevenue2 =
          totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] || 0;
        const percentPeriod2 = totalRevenue2
          ? (gameRevenue2 * 100) / totalRevenue2
          : 0;
        data[calculatedColumnsEnum.PERCENT_PER_GAME_PERIOD2] = percentPeriod2;

        data[calculatedColumnsEnum.COMPARE_REVENUE_PERCENT] =
          percentPeriod1 && percentPeriod2
            ? percentPeriod1 - percentPeriod2
            : this.$t("Display.notSignificant");

        return this.addProgressionStyles(data);
      });
    },
    completeTotals(totalsData) {
      const totalRevenue1 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] || 0;
      const totalRevenue2 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] || 0;
      totalsData[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] = totalRevenue2
        ? (totalRevenue1 / totalRevenue2 - 1) * 100
        : this.$t("Display.notSignificant");

      const totalRevenueHt1 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1] || 0;
      const totalRevenueHt2 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2] || 0;
      totalsData[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT] = totalRevenueHt2
        ? (totalRevenueHt1 / totalRevenueHt2 - 1) * 100
        : this.$t("Display.notSignificant");

      totalsData["column-class"] = {};
      totalsData["column-class"][
        calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS
      ] = "col-align-left";
      totalsData["column-class"][
        calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT
      ] = "col-align-left";

      return this.addProgressionStyles(totalsData);
    },
    addProgressionStyles(obj) {
      var cellVariants = {};

      const invoiceProgression =
        obj[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS];
      if (invoiceProgression && !isNaN(invoiceProgression)) {
        cellVariants[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] =
          invoiceProgression > 0 ? "success" : "danger";
      }

      const percentageProgression =
        obj[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT];
      if (percentageProgression && !isNaN(percentageProgression)) {
        cellVariants[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT] =
          percentageProgression > 0 ? "success" : "danger";
      }

      obj["_cellVariants"] = cellVariants;
      return obj;
    },
    downloadPdf() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData() {
      const self = this;
      var datasForExport = [];

      self.tablesData.forEach((table, index) => {
        const renderer = self.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || self.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          self.dimensionColumnsTables,
          self.measureColumnsTables,
          self.groupHeaders,
          table.tableHeader,
          sortKey,
          null,
          table.totalsData
        );
        datasForExport.push(data);
      });

      const siteLabels = self.siteValues.map((site) => site.label);

      TableTools.Helper.storeExportData(
        datasForExport,
        self.title,
        Tools.StringHelper.constructExportSubtitles(
          siteLabels,
          self.labelDatePeriod1,
          "",
          self.labelDatePeriod2
        ),
        Tools.StringHelper.constructExportFilename(
          self.title,
          null,
          self.dateRangeStr1
        )
      );
    },
  },
};
</script>
